// Check CHẨN ĐOÁN: hook telemetry của workspace này đã bao giờ chạy chưa?
//
// Khác hẳn check "Telemetry delivery (probe POST)" (#250) — probe đo đường RA (token,
// TLS, mạng) nên nó XANH kể cả khi không một event nào được sinh ra. Đúng ca
// 2026-09-16: `avatar init` ở thư mục con, user mở Claude Code ở thư mục cha ⇒ Claude
// Code không nạp hook nào ⇒ 0 event. Probe vẫn xanh vì đường ra vẫn tốt; thứ hỏng là
// KHÔNG CÓ GÌ để gửi. Check này đo đúng vế còn thiếu đó.
//
// ⛔ `fixable: false` VĨNH VIỄN. "Hook không chạy" có ít nhất 3 nguyên nhân khác nhau —
// workspace nằm sai chỗ, tool telemetry bị tắt, hoặc đơn giản là chưa mở phiên nào —
// và hai nguyên nhân sau KHÔNG phải lỗi. Cho `--fix` tự `init` đè lên là chữa sai
// bệnh, còn phá thì thật. Việc dời workspace đã có check RIÊNG lo (check A).
//
// HÀM THUẦN, 0 I/O: caller đọc file, hàm này chỉ luận. Test được mọi mốc thời gian
// bằng literal, không phải chạm đồng hồ thật.
import type { ClaudeSessionStatus } from "./detect-open-claude-code-session.js";

/**
 * Ngưỡng "telemetry đã chết" — 24 giờ. KHÔNG phải số tự nghĩ ra: chính pack khai nó
 * trong `tools/telemetry/hooks/shared/heartbeat.py` ("If mtime is stale (> 24h),
 * telemetry is silently dead"). Dùng lại để hai bên không bao giờ lệch định nghĩa.
 */
export const HEARTBEAT_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export interface TelemetryHookActivityFacts {
  /** `last.last_invocation_ts` của heartbeat, ĐÃ đổi sang ms. `null` = chưa có file/không đọc được. */
  heartbeatTsMs: number | null;
  /** `last.opted_out` — user tự tắt telemetry thì im lặng là ĐÚNG, không phải hỏng. */
  optedOut: boolean;
  /** mtime transcript Claude Code mới nhất của workspace (`detectOpenClaudeCodeSession`). */
  newestSessionMtimeMs: number | null;
  sessionStatus: ClaudeSessionStatus;
  /** Mốc "bây giờ" (ms). Tham số chứ không `Date.now()` trong thân hàm — giữ hàm THUẦN. */
  nowMs: number;
}

export interface TelemetryHookActivityVerdict {
  status: "ok" | "warn";
  detail: string;
}

function hoursBetween(a: number, b: number): string {
  return String(Math.round(Math.abs(a - b) / 3_600_000));
}

/**
 * Luận trạng thái hook từ 2 mốc: heartbeat (hook có chạy không) và transcript phiên
 * Claude Code (có ai mở workspace này không). Một mốc đứng một mình đều vô nghĩa —
 * heartbeat vắng mà chưa ai mở phiên thì không có gì sai cả.
 */
export function assessTelemetryHookActivity(
  facts: TelemetryHookActivityFacts,
): TelemetryHookActivityVerdict {
  // Không đọc được thư mục transcript ⇒ KHÔNG có mẫu số để so. Nói thẳng là không đo
  // được, đừng kết luận theo hướng nào.
  if (facts.sessionStatus === "unknown") {
    return {
      status: "warn",
      detail:
        "không đọc được transcript Claude Code của workspace này ⇒ KHÔNG kết luận được hook có chạy hay không.",
    };
  }

  // Chưa từng mở phiên Claude Code ⇒ chưa có gì để hook bám vào. Đây là trạng thái
  // BÌNH THƯỜNG của workspace vừa init, không phải sự cố.
  if (facts.newestSessionMtimeMs === null) {
    return {
      status: "ok",
      detail: "chưa có phiên Claude Code nào ở workspace này — chưa có gì để kết luận.",
    };
  }

  if (facts.heartbeatTsMs === null) {
    return {
      status: "warn",
      detail:
        "ĐÃ có phiên Claude Code ở workspace này nhưng hook telemetry CHƯA TỪNG chạy (không có .claude/state/telemetry-heartbeat.json) — 0 event được sinh ra. Nguyên nhân thường gặp: workspace Avatar nằm ở thư mục khác thư mục đang mở Claude Code, hoặc tool telemetry đang tắt ('avatar tools list').",
    };
  }

  if (facts.optedOut) {
    return {
      status: "ok",
      detail: `hook có chạy nhưng telemetry đang được tắt chủ động (opted out) — im lặng là đúng. Lần hook chạy cuối: ${hoursBetween(facts.nowMs, facts.heartbeatTsMs)}h trước.`,
    };
  }

  // Có phiên MỚI HƠN lần hook chạy cuối quá 1 ngày ⇒ phiên đó chạy mà không hook nào
  // bắn. So 2 mốc với nhau, KHÔNG so với `now`: workspace bỏ không cả tháng thì cả hai
  // mốc cùng cũ, và đó không phải lỗi.
  const gapMs = facts.newestSessionMtimeMs - facts.heartbeatTsMs;
  if (gapMs > HEARTBEAT_STALE_THRESHOLD_MS) {
    return {
      status: "warn",
      detail: `phiên Claude Code gần nhất mới hơn lần hook telemetry chạy cuối ${hoursBetween(facts.newestSessionMtimeMs, facts.heartbeatTsMs)}h ⇒ (các) phiên gần đây KHÔNG sinh event nào. Kiểm workspace có đúng ở thư mục đang mở Claude Code không, và 'avatar tools list' xem telemetry còn bật.`,
    };
  }

  return {
    status: "ok",
    detail: `hook telemetry có chạy — lần cuối cách phiên Claude Code gần nhất ${hoursBetween(facts.newestSessionMtimeMs, facts.heartbeatTsMs)}h.`,
  };
}
